/**
 * Monitoring and logging utilities using Sentry
 * Nuclear-grade type safety for production monitoring
 */

import * as Sentry from '@sentry/nextjs';

// Nuclear-grade monitoring types
type SerializableValue = string | number | boolean | null | undefined | SerializableObject | SerializableArray;
export type SerializableObject = { [key: string]: SerializableValue };
type SerializableArray = SerializableValue[];

export function toSerializable(input: unknown): SerializableObject {
  try {
    return JSON.parse(JSON.stringify(input ?? {})) as SerializableObject;
  } catch {
    return {};
  }
}

/**
 * Sensitive field patterns for PII redaction
 */
const SENSITIVE_PATTERNS = [
  /email/i,
  /password/i,
  /token/i,
  /secret/i,
  /key/i,
  /authorization/i,
  /cookie/i,
  /apikey/i,
  /api_key/i,
  /bearer/i,
  /session/i,
  /credentials?/i,
  /ssn/i,
  /credit[-_]?card/i,
  /cvv/i,
  /pin/i,
];

/**
 * Redact sensitive fields from telemetry data
 * Scrubs emails, tokens, keys, secrets, authorization headers
 */
export function redactSensitive(data: SerializableObject): SerializableObject {
  const redacted: SerializableObject = {};

  for (const [key, value] of Object.entries(data)) {
    // Check if key matches sensitive pattern
    const isSensitive = SENSITIVE_PATTERNS.some(pattern => pattern.test(key));

    if (isSensitive) {
      // Redact sensitive values
      if (typeof value === 'string') {
        // Keep first 4 chars for debugging, redact rest
        redacted[key] = value.length > 4 ? `${value.substring(0, 4)}***` : '***';
      } else {
        redacted[key] = '[REDACTED]';
      }
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively redact nested objects
      redacted[key] = redactSensitive(value as SerializableObject);
    } else if (Array.isArray(value)) {
      // Redact array elements if they're objects
      redacted[key] = value.map(item =>
        item !== null && typeof item === 'object'
          ? redactSensitive(item as SerializableObject)
          : item
      );
    } else {
      redacted[key] = value;
    }
  }

  return redacted;
}

function logDevelopmentEvent(level: Sentry.SeverityLevel, message: string, data?: SerializableObject) {
  if (process.env.NODE_ENV === 'development') {
    Sentry.addBreadcrumb({
      level,
      category: 'development-log',
      message,
      data,
      timestamp: Date.now() / 1000,
    });
  }
}

/**
 * Sampling rates by level (0.0-1.0)
 * Configurable via environment variables
 */
const SAMPLE_RATES = {
  info: parseFloat(process.env.TELEMETRY_SAMPLE_RATE_INFO ?? '0.1'), // 10%
  warning: parseFloat(process.env.TELEMETRY_SAMPLE_RATE_WARNING ?? '0.5'), // 50%
  error: 1.0, // Always capture errors
};

/**
 * Check if event should be sampled based on level
 */
function shouldSample(level: 'info' | 'warning' | 'error'): boolean {
  const rate = SAMPLE_RATES[level];
  return Math.random() < rate;
}

/**
 * Build consistent tag shape for telemetry
 */
export function buildTags(
  component: string,
  operation: string,
  extra?: Record<string, string>
): Record<string, string> {
  return {
    component,
    operation,
    ...extra,
  };
}

interface MonitoringContext {
  user?: { id: string; email?: string };
  tags?: Record<string, string>;
  extra?: SerializableObject;
}

interface ExtendedUser {
  id: string;
  email?: string;
  username?: string;
  role?: string;
  companyId?: string;
  lastActivity?: string;
  permissions?: string[];
}

/**
 * Capture an error with context
 * Automatically redacts sensitive data
 */
export async function captureError(
  error: unknown,
  context?: MonitoringContext
): Promise<string> {
  if (context?.user) {
    Sentry.setUser(context.user);
  }

  if (context?.tags) {
    Sentry.setTags(context.tags);
  }

  if (context?.extra) {
    // Apply redaction to extras
    const redactedExtras = redactSensitive(context.extra);
    Sentry.setExtras(redactedExtras);
  }

  const normalizedError = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');
  const eventId = Sentry.captureException(normalizedError);

  logDevelopmentEvent('error', 'Error captured', toSerializable({
    message: normalizedError.message,
    stack: normalizedError.stack,
    tags: context?.tags,
    extra: context?.extra,
  }));

  return eventId;
}

/**
 * Capture a message with level
 * Automatically applies sampling and redaction
 */
export async function captureMessage(
  message: string,
  level?: 'info' | 'warning' | 'error'
): Promise<string>;
export async function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error',
  context: { tags?: Record<string, string>; extra?: SerializableObject }
): Promise<string>;
export async function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: { tags?: Record<string, string>; extra?: SerializableObject }
): Promise<string> {
  // Apply sampling (errors always captured)
  if (!shouldSample(level)) {
    // Return empty event ID for sampled-out events
    return '';
  }

  const eventId = Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (context?.tags) {
      scope.setTags(context.tags);
    }

    if (context?.extra) {
      // Apply redaction to extras
      const redactedExtras = redactSensitive(context.extra);
      scope.setExtras(redactedExtras);
    }

    return Sentry.captureMessage(message);
  });

  logDevelopmentEvent(level, message, context?.extra ?? {});

  return eventId;
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(
  message: string,
  category: string = 'user',
  level: 'info' | 'warning' | 'error' = 'info',
  data?: SerializableObject
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Set user context
 */
export function setUser(user: ExtendedUser) {
  Sentry.setUser(user);
}

/**
 * Clear user context (on logout)
 */
export function clearUser() {
  Sentry.setUser(null);
}

export interface LoggerContext {
  tags?: Record<string, string>;
  extra?: SerializableObject | Record<string, unknown>;
}

function isLoggerContext(value: unknown): value is LoggerContext {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  if (value instanceof Error) {
    return false;
  }
  return 'tags' in (value as Record<string, unknown>) || 'extra' in (value as Record<string, unknown>);
}

function ensureError(input: unknown, fallbackMessage = 'Unknown error'): Error {
  if (input instanceof Error) {
    return input;
  }
  if (typeof input === 'string') {
    return new Error(input);
  }

  const error = new Error(fallbackMessage);
  try {
    (error as Error & { cause?: unknown }).cause = input;
  } catch {
    // no-op if cause assignment fails
  }
  return error;
}

function buildMonitoringContext(
  context?: LoggerContext,
  additions?: Record<string, unknown>
): MonitoringContext | undefined {
  const tags = context?.tags;

  const baseExtra =
    context?.extra && typeof context.extra === 'object' ? (context.extra as Record<string, unknown>) : undefined;

  const combinedExtra = {
    ...(baseExtra ?? {}),
    ...(additions ?? {}),
  };

  const hasExtra = Object.keys(combinedExtra).length > 0;

  if (!tags && !hasExtra) {
    return undefined;
  }

  return {
    ...(tags ? { tags } : {}),
    ...(hasExtra ? { extra: toSerializable(combinedExtra) } : {}),
  };
}

function resolveErrorArguments(
  errOrMsg: unknown,
  maybeErrorOrCtx?: unknown,
  maybeCtx?: LoggerContext
): { error: Error; context?: LoggerContext; message?: string } {
  let context: LoggerContext | undefined;
  let message: string | undefined;
  let error: Error;

  const assignContext = (candidate: unknown) => {
    if (isLoggerContext(candidate)) {
      context = candidate;
      return true;
    }
    return false;
  };

  if (typeof errOrMsg === 'string') {
    message = errOrMsg;

    if (maybeErrorOrCtx instanceof Error) {
      error = maybeErrorOrCtx;
      assignContext(maybeCtx);
      return { error, context, message };
    }

    if (assignContext(maybeErrorOrCtx)) {
      error = new Error(errOrMsg);
      return { error, context, message };
    }

    if (maybeErrorOrCtx !== undefined) {
      error = ensureError(maybeErrorOrCtx, errOrMsg);
      assignContext(maybeCtx);
      return { error, context, message };
    }

    error = new Error(errOrMsg);
    assignContext(maybeCtx);
    return { error, context, message };
  }

  if (errOrMsg instanceof Error) {
    error = errOrMsg;

    if (assignContext(maybeErrorOrCtx)) {
      return { error, context };
    }

    if (typeof maybeErrorOrCtx === 'string') {
      message = maybeErrorOrCtx;
      assignContext(maybeCtx);
      return { error, context, message };
    }

    assignContext(maybeCtx);
    return { error, context };
  }

  if (maybeErrorOrCtx instanceof Error) {
    error = maybeErrorOrCtx;
    assignContext(maybeCtx);
    return { error, context };
  }

  error = ensureError(errOrMsg);

  if (assignContext(maybeErrorOrCtx)) {
    return { error, context };
  }

  if (typeof maybeErrorOrCtx === 'string') {
    message = maybeErrorOrCtx;
    assignContext(maybeCtx);
    return { error, context, message };
  }

  assignContext(maybeCtx);
  return { error, context };
}

export const logger = {
  info(message: string, context?: LoggerContext) {
    const monitoringContext = buildMonitoringContext(context);
    if (monitoringContext) {
      return captureMessage(message, 'info', monitoringContext);
    }
    return captureMessage(message, 'info');
  },
  warn(message: string, context?: LoggerContext) {
    const monitoringContext = buildMonitoringContext(context);
    if (monitoringContext) {
      return captureMessage(message, 'warning', monitoringContext);
    }
    return captureMessage(message, 'warning');
  },
  error(errOrMsg: unknown, maybeErrorOrCtx?: unknown, maybeCtx?: LoggerContext) {
    const { error, context, message } = resolveErrorArguments(errOrMsg, maybeErrorOrCtx, maybeCtx);
    const monitoringContext = buildMonitoringContext(
      context,
      message ? { message } : undefined
    );

    if (monitoringContext) {
      return captureError(error, monitoringContext);
    }
    return captureError(error);
  },
};

(globalThis as unknown as {
  captureError: typeof captureError;
  captureMessage: typeof captureMessage;
  toSerializable: typeof toSerializable;
}).captureError = captureError;

(globalThis as unknown as {
  captureError: typeof captureError;
  captureMessage: typeof captureMessage;
  toSerializable: typeof toSerializable;
}).captureMessage = captureMessage;

(globalThis as unknown as {
  captureError: typeof captureError;
  captureMessage: typeof captureMessage;
  toSerializable: typeof toSerializable;
}).toSerializable = toSerializable;
